/**
 * STATSMONITOR.JSX - Monitoring des Performances
 * 
 * Ce composant affiche des statistiques de performance en temps réel
 * dans Leva pour surveiller le rendu 3D.
 * 
 * MÉTRIQUES AFFICHÉES :
 * - FPS : Images par seconde
 * - Frame Time : Temps de rendu par frame (ms)
 * - Memory : Utilisation mémoire (si disponible)
 * - Triangles : Nombre de triangles dans la scène
 * - Draw Calls : Nombre d'appels de rendu
 * 
 * UTILISATION :
 * Placer ce composant dans la scène R3F pour activer le monitoring.
 */

import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useControls } from 'leva'

function StatsMonitor() {
  const { gl } = useThree()
  
  // Références pour le calcul du FPS
  const frameCount = useRef(0)
  const lastTime = useRef(performance.now())

  // Contrôles Leva avec des valeurs initiales
  const [, set] = useControls(
    'Performance Stats',
    () => ({
      FPS: {
        value: 60,
        label: '📊 FPS',
        disabled: true
      },
      frameTime: {
        value: '16.67 ms',
        label: '⏱️ Frame Time',
        disabled: true
      },
      triangles: {
        value: '0',
        label: '🔺 Triangles',
        disabled: true
      },
      drawCalls: {
        value: 0,
        label: '🎨 Draw Calls',
        disabled: true
      },
      memory: {
        value: '0 MB',
        label: '💾 Memory (JS)',
        disabled: true
      }
    }),
    { collapsed: true }
  )

  // useFrame pour calculer les stats à chaque frame
  useFrame(() => {
    frameCount.current++

    const currentTime = performance.now()
    const deltaTime = currentTime - lastTime.current

    // Mettre à jour les stats toutes les 500ms
    if (deltaTime >= 500) {
      const newFps = Math.round((frameCount.current * 1000) / deltaTime)
      const newFrameTime = parseFloat((deltaTime / frameCount.current).toFixed(2))
      
      // Stats WebGL (disponibles via gl.info)
      const newTriangles = gl.info?.render?.triangles || 0
      const newDrawCalls = gl.info?.render?.calls || 0
      
      // Mémoire (si performance.memory est disponible - Chrome uniquement)
      const newMemory = performance.memory 
        ? Math.round(performance.memory.usedJSHeapSize / 1048576)
        : 0

      // Mise à jour des valeurs Leva sans recréer le panel
      set({
        FPS: newFps,
        frameTime: `${newFrameTime} ms`,
        triangles: newTriangles.toLocaleString(),
        drawCalls: newDrawCalls,
        memory: `${newMemory} MB`
      })
      
      // Debug: afficher les FPS dans la console toutes les 5 secondes
      if (Math.floor(currentTime / 5000) !== Math.floor(lastTime.current / 5000)) {
      }
      
      frameCount.current = 0
      lastTime.current = currentTime
    }
  })

  // Ce composant ne rend rien visuellement
  return null
}

export default StatsMonitor

